/* eslint-disable camelcase */
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { DemoDataService } from '../demo-data.service';
import { GroupLinkService } from '../group-link.service';
import { TransactionService } from '../transaction.service';
import { OKTA_LOGIN_IN_PROGRESS_SESSION_KEY } from '@core/okta-integration/okta-session-keys';
import {
  ANALYTICS_CANCEL_REASONS,
  ANALYTICS_EVENTS,
  ANALYTICS_JOURNEY_ORIGINS,
  ANALYTICS_MAX_ERROR_MESSAGE_LENGTH,
  ANALYTICS_MAX_PARAMETER_VALUE_LENGTH,
  ANALYTICS_SCREENS,
} from './analytics.events';
import type { IAnalyticsService } from './analytics.interface';
import type {
  AbandonParameters,
  AddShippingInfoParameters,
  AddToCartParameters,
  AdlinkClickParameters,
  AnalyticsItem,
  BeginCheckoutParameters,
  CheckoutBackParameters,
  CheckoutCompletedParameters,
  CheckoutFormInvalidParameters,
  DiscountAppliedParameters,
  ErrorParameters,
  PurchaseParameters,
  RainoutInsuranceToggledParameters,
  RemoveFromCartParameters,
  UpgradeAcquiredParameters,
  UpgradeShownParameters,
  UpsellSelectedParameters,
} from './analytics.types';
import { toOptionalId } from './analytics.utility';

@Injectable({ providedIn: 'root' })
export class AnalyticsService implements IAnalyticsService {
  private static readonly JOURNEY_ORIGIN_KEY = 'gsAnalyticsJourneyOrigin';
  private static readonly REPORTED_PURCHASES_KEY =
    'gsAnalyticsReportedPurchases';

  private readonly router = inject(Router);
  private readonly groupLinkService = inject(GroupLinkService);
  private readonly transactionService = inject(TransactionService);
  private readonly demoDataService = inject(DemoDataService);

  private checkoutBackInProgress = false;
  private pendingAbandon: AbandonParameters | null = null;
  private pendingAbandonOwner: string | null = null;
  private readonly reportedAbandonments = new Set<string>();
  private readonly startedForms = new Set<string>();
  private readonly completedTransactions = new Set<string>();
  private readonly reportedPurchases = new Set<string>();
  private readonly reportedUpgradeAcquisitions = new Set<string>();
  private readonly reportedUpgradeShown = new Set<string>();
  private checkoutFormInvalidAttempts = 0;
  private hasReportedShippingInfo = false;
  private previousScreen: string | null = null;
  private pendingCancelReason: string | null = null;
  private lastCheckoutTransactionId: string | null = null;
  private lastJourneyOriginSnapshot: string | null = null;

  public constructor() {
    this.router.events
      .pipe(filter(navigationEvent => navigationEvent instanceof NavigationEnd))
      .subscribe({
        next: navigationEvent => {
          const path = (navigationEvent as NavigationEnd).urlAfterRedirects;
          this.trackScreenView(this.resolveScreenName(path), path);
        },
        error: (error: unknown) => {
          console.error('Unexpected Router events stream error', error);
        },
      });

    globalThis.addEventListener('pagehide', () => {
      if (this.isOktaRedirectInFlight()) {
        return;
      }
      if (this.pendingAbandon) {
        this.trackAbandon(this.pendingAbandon);
      }
    });
  }

  private isOktaRedirectInFlight(): boolean {
    try {
      return (
        sessionStorage.getItem(OKTA_LOGIN_IN_PROGRESS_SESSION_KEY) !== null
      );
    } catch {
      return false;
    }
  }

  public trackScreenView(screenName: string, path: string): void {
    this.detectTransactionCancellation(screenName);

    if (screenName === ANALYTICS_SCREENS.CHECKOUT) {
      this.lastCheckoutTransactionId = this.parseCheckoutTransactionId(path);
    }

    this.setGlobalParameters({
      page_title: screenName,
      page_location: globalThis.location.href,
    });

    this.gtag('event', ANALYTICS_EVENTS.PAGE_VIEW, {
      page_title: screenName,
      page_path: path,
      page_location: globalThis.location.href,
    });

    this.previousScreen = screenName;
  }

  public trackViewItemList(listName: string, items: AnalyticsItem[]): void {
    this.gtag('event', ANALYTICS_EVENTS.VIEW_ITEM_LIST, {
      item_list_name: listName,
      items: this.toGa4Items(items),
    });
  }

  public trackSelectItem(listName: string, item: AnalyticsItem): void {
    this.rememberJourneyOrigin();
    this.gtag('event', ANALYTICS_EVENTS.SELECT_ITEM, {
      item_list_name: listName,
      items: this.toGa4Items([item]),
      game_id: item.itemId,
      game_name: item.itemName,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackViewItem(item: AnalyticsItem): void {
    this.gtag('event', ANALYTICS_EVENTS.VIEW_ITEM, {
      items: this.toGa4Items([item]),
      game_id: item.itemId,
      game_name: item.itemName,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackAddToCart(parameters: AddToCartParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.ADD_TO_CART, {
      currency: parameters.currency,
      value: parameters.value,
      items: this.toGa4Items(parameters.items),
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      seat_count: parameters.seatCount,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackRemoveFromCart(parameters: RemoveFromCartParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.REMOVE_FROM_CART, {
      currency: parameters.currency,
      value: parameters.value,
      items: this.toGa4Items(parameters.items),
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      seat_count: parameters.seatCount,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackBeginCheckout(parameters: BeginCheckoutParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
      transaction_id: parameters.transactionId,
      game_id: parameters.gameId,
      currency: parameters.currency,
      value: parameters.value,
      fees: parameters.fees,
      seat_count: parameters.seatCount,
      items: this.toGa4Items(parameters.items),
      ...(parameters.buyerType !== undefined && {
        buyer_type: parameters.buyerType,
      }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackFormStart(formName: string): void {
    if (this.startedForms.has(formName)) {
      return;
    }
    this.startedForms.add(formName);

    const parameters = {
      form_name: formName,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    };
    this.gtag('event', ANALYTICS_EVENTS.FORM_START, parameters);
    this.gtag('event', ANALYTICS_EVENTS.FORM_START_CHECKOUT, parameters);
  }

  public trackLogin(method: string): void {
    this.gtag('event', ANALYTICS_EVENTS.LOGIN, { method });
  }

  public trackLogout(method: string): void {
    this.gtag('event', ANALYTICS_EVENTS.LOGOUT, { method });
  }

  public trackAddShippingInfo(parameters: AddShippingInfoParameters): void {
    if (this.hasReportedShippingInfo) {
      return;
    }
    this.hasReportedShippingInfo = true;

    this.gtag('event', ANALYTICS_EVENTS.ADD_SHIPPING_INFO, {
      currency: parameters.currency,
      value: parameters.value,
      items: this.toGa4Items(parameters.items),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackAddPaymentInfo(
    paymentType: string,
    value: number,
    currency: string,
    items: AnalyticsItem[]
  ): void {
    this.gtag('event', ANALYTICS_EVENTS.ADD_PAYMENT_INFO, {
      payment_type: paymentType,
      currency,
      value,
      items: this.toGa4Items(items),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackUpsellSelected(parameters: UpsellSelectedParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.UPSELL_SELECTED, {
      ...this.upsellGa4Parameters(parameters),
    });
  }

  public trackUpsellDeselected(parameters: UpsellSelectedParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.UPSELL_DESELECTED, {
      ...this.upsellGa4Parameters(parameters),
    });
  }

  private upsellGa4Parameters(
    parameters: UpsellSelectedParameters
  ): Record<string, unknown> {
    return {
      upsell_id: parameters.upsellId,
      upsell_name: parameters.upsellName,
      value: parameters.value,
      game_id: parameters.gameId,
      transaction_id: parameters.transactionId,
      ...(toOptionalId(parameters.previousCartId) !== undefined && {
        previous_cart_id: toOptionalId(parameters.previousCartId),
      }),
      ...(toOptionalId(parameters.newCartId) !== undefined && {
        new_cart_id: toOptionalId(parameters.newCartId),
      }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    };
  }

  public trackRainoutInsuranceToggled(
    parameters: RainoutInsuranceToggledParameters
  ): void {
    this.gtag('event', ANALYTICS_EVENTS.RAINOUT_INSURANCE_TOGGLED, {
      action: parameters.action,
      insurance_price: parameters.insurancePrice,
      transaction_id: parameters.transactionId,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackDiscountApplied(parameters: DiscountAppliedParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.DISCOUNT_APPLIED, {
      action: parameters.action ?? 'applied',
      discount_amount: parameters.discountAmount,
      transaction_id: parameters.transactionId,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackCheckoutCompleted(parameters: CheckoutCompletedParameters): void {
    if (this.completedTransactions.has(parameters.transactionId)) {
      return;
    }
    this.completedTransactions.add(parameters.transactionId);

    this.gtag(
      'event',
      ANALYTICS_EVENTS.CHECKOUT_COMPLETED,
      this.checkoutCompletedGa4Parameters(parameters)
    );
  }

  public trackPurchase(parameters: PurchaseParameters): void {
    if (this.hasReportedPurchase(parameters.transactionId)) {
      return;
    }
    this.rememberReportedPurchase(parameters.transactionId);

    this.gtag('event', ANALYTICS_EVENTS.PURCHASE, {
      ...this.checkoutCompletedGa4Parameters(parameters),
      items: this.toGa4Items(parameters.items),
    });
    this.resetJourneyState();
  }

  public trackUpgradeShown(parameters: UpgradeShownParameters): void {
    if (this.reportedUpgradeShown.has(parameters.upgradeId)) {
      return;
    }
    this.reportedUpgradeShown.add(parameters.upgradeId);

    this.gtag('event', ANALYTICS_EVENTS.UPGRADE_SHOWN, {
      upgrade_id: parameters.upgradeId,
      upgrade_name: parameters.upgradeName,
      goal: parameters.goal,
      min_seats_to_show: parameters.minSeatsToShow,
      seats_in_cart: parameters.seatsInCart,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackUpgradeAcquired(parameters: UpgradeAcquiredParameters): void {
    if (this.reportedUpgradeAcquisitions.has(parameters.upgradeId)) {
      return;
    }
    this.reportedUpgradeAcquisitions.add(parameters.upgradeId);

    this.gtag('event', ANALYTICS_EVENTS.UPGRADE_ACQUIRED, {
      upgrade_id: parameters.upgradeId,
      upgrade_name: parameters.upgradeName,
      goal: parameters.goal,
      seats_in_cart: parameters.seatsInCart,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackAdlinkClick(parameters: AdlinkClickParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.ADLINK_CLICK, {
      adlink_id: parameters.adlinkId,
      adlink_name: parameters.adlinkName,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...(parameters.transactionId !== undefined && {
        transaction_id: parameters.transactionId,
      }),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackCheckoutFormInvalid(
    parameters: CheckoutFormInvalidParameters
  ): void {
    this.checkoutFormInvalidAttempts += 1;

    this.gtag('event', ANALYTICS_EVENTS.CHECKOUT_FORM_INVALID, {
      invalid_fields: parameters.invalidFields
        .join(',')
        .slice(0, ANALYTICS_MAX_PARAMETER_VALUE_LENGTH),
      invalid_field_count: parameters.invalidFields.length,
      attempt_number: this.checkoutFormInvalidAttempts,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...(parameters.transactionId !== undefined && {
        transaction_id: parameters.transactionId,
      }),
      ...(parameters.value !== undefined && { value: parameters.value }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackError(parameters: ErrorParameters): void {
    const errorMessage = parameters.errorMessage ?? '';

    this.gtag('event', parameters.eventName, {
      error_type: parameters.errorType,
      error_message: errorMessage.slice(0, ANALYTICS_MAX_ERROR_MESSAGE_LENGTH),
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...(parameters.transactionId !== undefined && {
        transaction_id: parameters.transactionId,
      }),
      ...(parameters.value !== undefined && { value: parameters.value }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackAbandon(parameters: AbandonParameters): void {
    if (this.checkoutBackInProgress) {
      this.checkoutBackInProgress = false;
      this.clearPendingAbandon();
      this.resetJourneyMilestones();
      return;
    }
    const abandonmentKey = [
      parameters.stage,
      parameters.gameId ?? '',
      parameters.transactionId ?? '',
    ].join(':');
    if (this.reportedAbandonments.has(abandonmentKey)) {
      return;
    }
    this.reportedAbandonments.add(abandonmentKey);
    this.clearPendingAbandon();

    const eventName =
      parameters.stage === 'cart'
        ? ANALYTICS_EVENTS.CART_ABANDONED
        : ANALYTICS_EVENTS.CHECKOUT_ABANDONED;

    this.gtag('event', eventName, {
      stage: parameters.stage,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...(parameters.transactionId !== undefined && {
        transaction_id: parameters.transactionId,
      }),
      ...(parameters.value !== undefined && { value: parameters.value }),
      ...(parameters.currency !== undefined && {
        currency: parameters.currency,
      }),
      ...(parameters.seatCount !== undefined && {
        seat_count: parameters.seatCount,
      }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });

    this.lastJourneyOriginSnapshot = this.readJourneyOrigin();
    this.clearJourneyOrigin();
    this.resetJourneyMilestones();
  }

  public setPendingAbandon(
    owner: string,
    parameters: AbandonParameters | null
  ): void {
    if (
      this.pendingAbandonOwner !== null &&
      this.pendingAbandonOwner !== owner
    ) {
      return;
    }
    this.pendingAbandon = parameters;
    this.pendingAbandonOwner = parameters === null ? null : owner;
  }

  public isPendingAbandonArmed(owner: string): boolean {
    return this.pendingAbandonOwner === owner;
  }

  public trackCheckoutBack(parameters: CheckoutBackParameters): void {
    this.checkoutBackInProgress = true;
    this.pendingCancelReason = ANALYTICS_CANCEL_REASONS.CHECKOUT_BACK;

    this.gtag('event', ANALYTICS_EVENTS.CHECKOUT_BACK, {
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      transaction_id: parameters.transactionId,
      ...(parameters.value !== undefined && { value: parameters.value }),
      ...(parameters.currency !== undefined && {
        currency: parameters.currency,
      }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public setPendingCancelReason(reason: string): void {
    this.pendingCancelReason = reason;
  }

  private detectTransactionCancellation(newScreen: string): void {
    const isBackwardFromCheckout =
      this.previousScreen === ANALYTICS_SCREENS.CHECKOUT &&
      (newScreen === ANALYTICS_SCREENS.SEAT_SELECTION ||
        newScreen === ANALYTICS_SCREENS.EVENTS_LIST);

    if (!isBackwardFromCheckout) {
      return;
    }

    const transaction = this.transactionService.transaction$.value?.transaction;
    const transactionId =
      toOptionalId(transaction?.id) ??
      this.lastCheckoutTransactionId ??
      undefined;
    if (!transactionId) {
      return;
    }
    if (
      this.completedTransactions.has(transactionId) ||
      this.hasReportedPurchase(transactionId)
    ) {
      return;
    }

    const reason = this.pendingCancelReason ?? ANALYTICS_CANCEL_REASONS.UNKNOWN;
    this.pendingCancelReason = null;
    this.lastCheckoutTransactionId = null;

    const gameId = toOptionalId(transaction?.event?.pvEventId);
    const value = transaction?.tdcTransaction?.balance;

    this.gtag('event', ANALYTICS_EVENTS.TRANSACTION_CANCELLED, {
      transaction_id: transactionId,
      reason,
      ...(gameId !== undefined && { game_id: gameId }),
      ...(value !== undefined && { value }),
      seat_count: this.transactionService.getTotalSeats(),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  private rememberJourneyOrigin(): void {
    this.lastJourneyOriginSnapshot = null;
    if (this.readJourneyOrigin()) {
      return;
    }
    const origin = this.groupLinkService.getGroupLink()
      ? ANALYTICS_JOURNEY_ORIGINS.GROUP_LINK
      : ANALYTICS_JOURNEY_ORIGINS.EVENTS_LIST;
    try {
      sessionStorage.setItem(AnalyticsService.JOURNEY_ORIGIN_KEY, origin);
    } catch {
      // Private-mode / disabled storage: attribution is lost, tracking must not break.
    }
  }

  private journeyOriginParameter(): Record<string, string> {
    const origin = this.readJourneyOrigin() ?? this.lastJourneyOriginSnapshot;
    return origin ? { journey_origin: origin } : {};
  }

  private readJourneyOrigin(): string | null {
    try {
      return sessionStorage.getItem(AnalyticsService.JOURNEY_ORIGIN_KEY);
    } catch {
      return null;
    }
  }

  private clearPendingAbandon(): void {
    this.pendingAbandon = null;
    this.pendingAbandonOwner = null;
  }

  private resetJourneyState(): void {
    this.clearJourneyOrigin();
    this.lastJourneyOriginSnapshot = null;
    this.clearPendingAbandon();
    this.reportedAbandonments.clear();
    this.resetJourneyMilestones();
  }

  private resetJourneyMilestones(): void {
    this.startedForms.clear();
    this.reportedUpgradeAcquisitions.clear();
    this.reportedUpgradeShown.clear();
    this.checkoutFormInvalidAttempts = 0;
    this.hasReportedShippingInfo = false;
  }

  private clearJourneyOrigin(): void {
    try {
      sessionStorage.removeItem(AnalyticsService.JOURNEY_ORIGIN_KEY);
    } catch {
      // Nothing to clear if storage is unavailable.
    }
  }

  private hasReportedPurchase(transactionId: string): boolean {
    return (
      this.reportedPurchases.has(transactionId) ||
      this.readReportedPurchaseIds().includes(transactionId)
    );
  }

  private rememberReportedPurchase(transactionId: string): void {
    this.reportedPurchases.add(transactionId);
    try {
      const ids = this.readReportedPurchaseIds();
      if (!ids.includes(transactionId)) {
        ids.push(transactionId);
        sessionStorage.setItem(
          AnalyticsService.REPORTED_PURCHASES_KEY,
          JSON.stringify(ids)
        );
      }
    } catch {
      // Private-mode / disabled storage: falls back to in-memory-only dedup for this tab.
    }
  }

  private readReportedPurchaseIds(): string[] {
    try {
      const raw = sessionStorage.getItem(
        AnalyticsService.REPORTED_PURCHASES_KEY
      );
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  }

  private groupLinkSlugParameter(): Record<string, string> {
    const slug = toOptionalId(
      this.groupLinkService.getGroupLink() ?? undefined
    );
    return slug !== undefined ? { group_link_slug: slug } : {};
  }

  private checkoutCompletedGa4Parameters(
    parameters: CheckoutCompletedParameters
  ): Record<string, unknown> {
    return {
      transaction_id: parameters.transactionId,
      ...(toOptionalId(parameters.tdcTransactionId) !== undefined && {
        tdc_transaction_id: toOptionalId(parameters.tdcTransactionId),
      }),
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      item_count: parameters.itemCount,
      seat_count: parameters.seatCount,
      has_upsell: parameters.hasUpsell,
      has_rainout_insurance: parameters.hasRainoutInsurance,
      discount_applied: parameters.discountApplied,
      value: parameters.value,
      fees: parameters.fees,
      currency: parameters.currency,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    };
  }

  private toGa4Items(items: AnalyticsItem[]): Record<string, unknown>[] {
    return items.map(item => ({
      item_id: item.itemId,
      item_name: item.itemName,
      ...(item.price !== undefined && { price: item.price }),
      ...(item.quantity !== undefined && { quantity: item.quantity }),
      ...(item.index !== undefined && { index: item.index }),
      ...(item.itemCategory !== undefined && {
        item_category: item.itemCategory,
      }),
      ...(item.itemListName !== undefined && {
        item_list_name: item.itemListName,
      }),
    }));
  }

  private gtag(
    command: string,
    eventName: string,
    parameters: Record<string, unknown>
  ): void {
    this.callGtag(command, eventName, parameters);
  }

  private setGlobalParameters(parameters: Record<string, unknown>): void {
    this.callGtag('set', parameters);
  }

  private callGtag(...callArguments: unknown[]): void {
    if (this.demoDataService.isDemoActive()) {
      return;
    }
    const gtagFunction = (
      globalThis as unknown as { gtag?: (...callArguments: unknown[]) => void }
    ).gtag;
    if (typeof gtagFunction !== 'function') {
      return;
    }
    gtagFunction(...callArguments);
  }

  private resolveScreenName(url: string): string {
    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean);

    if (segments.includes('checkout')) {
      return ANALYTICS_SCREENS.CHECKOUT;
    }
    if (segments.includes('summary')) {
      return ANALYTICS_SCREENS.CHECKOUT_CONFIRMATION;
    }
    if (segments.includes('seat-selection')) {
      return ANALYTICS_SCREENS.SEAT_SELECTION;
    }
    if (segments.includes('events')) {
      return ANALYTICS_SCREENS.EVENTS_LIST;
    }
    return ANALYTICS_SCREENS.UNKNOWN;
  }

  private parseCheckoutTransactionId(path: string): string | null {
    const segments = path.split('?')[0].split('/').filter(Boolean);
    const checkoutIndex = segments.indexOf('checkout');
    return checkoutIndex !== -1 ? (segments[checkoutIndex + 1] ?? null) : null;
  }
}
